import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../assets/css/CampaignList.css";
import { fetchWithToken } from "../utils/fetchWithToken";

// Parsers auxiliares
const parsePercentage = (str) =>
  parseFloat(str?.replace("%", "").replace(",", ".")) || 0;
const parseCurrency = (str) =>
  parseFloat(str?.replace("R$", "").replace(",", ".")) || 0;

// Parser para datas no formato "DD/MM/YYYY" (vindo da API)
const parseDate = (str) => {
  if (!str) return null;
  const [day, month, year] = str.split("/");
  return new Date(Number(year), Number(month) - 1, Number(day));
};

// Parser para strings ISO "YYYY-MM-DD" (do <input type="date">)
const parseIsoToLocalDate = (iso) => {
  if (!iso) return null;
  const [year, month, day] = iso.split("-");
  return new Date(Number(year), Number(month) - 1, Number(day));
};

function CampaignItem({
  campaign,
  rawCampaign,
  averages,
  totalAverages,
  allCampaigns,
}) {
  const { ctr, cpc, conversionRate, prioridade, name, compras, roas } =
    campaign;
  const navigate = useNavigate();

  // Converte strings para números
  const conversionRateNumber = parsePercentage(conversionRate);
  const ctrNumber = parsePercentage(ctr);
  const cpcNumber = parseCurrency(cpc);
  const roasNumber = parseCurrency(roas);
  const comprasNumber = parseCurrency(compras);

  // Comparação com médias
  const comparison = {
    conversionRate:
      conversionRateNumber >= averages.avgConversionRate ? "acima" : "abaixo",
    ctr: ctrNumber >= averages.avgCtr ? "acima" : "abaixo",
    cpc: cpcNumber <= averages.avgCpc ? "abaixo" : "acima",
    roas: roasNumber >= averages.avgRoas ? "acima" : "abaixo",
    compras:
      comprasNumber >= averages.avgCompras ? "acima" : "abaixo",
  };

  // Diferença percentual
  const diffPercentages = {
    conversionRate: averages.avgConversionRate
      ? Math.abs(
        ((conversionRateNumber - averages.avgConversionRate) /
          averages.avgConversionRate) *
        100
      )
      : 0,
    ctr: averages.avgCtr
      ? Math.abs(((ctrNumber - averages.avgCtr) / averages.avgCtr) * 100)
      : 0,
    cpc: averages.avgCpc
      ? Math.abs(((cpcNumber - averages.avgCpc) / averages.avgCpc) * 100)
      : 0,
    roas: averages.avgRoas
      ? Math.abs(((roasNumber - averages.avgRoas) / averages.avgRoas) * 100)
      : 0,
    compras: averages.avgCompras
      ? Math.abs(((comprasNumber - averages.avgCompras) / averages.avgCompras) * 100)
      : 0,
  };

  const getPriorityClass = () => {
    if (prioridade?.toLowerCase().includes("p1")) return "priority-red";
    if (prioridade?.toLowerCase().includes("p2")) return "priority-yellow";
    if (prioridade?.toLowerCase().includes("normal"))
      return "priority-green";
    return "priority-default";
  };

  const formatNumberBR = (num) => {
    if (isNaN(num)) return "0,00";
    return num.toFixed(2).replace('.', ',');
  };

  const handleClick = () => {
    navigate("campaign-detail", {
      state: {
        campaign,
        rawCampaign,
        averages,
        totalAverages,
        comparison,
        diffPercentages,
        allCampaigns,
      },
    });
  };

  return (
    <div className="campaign-item-container">
      <div className="campaign-item">
        <div className="campaign-name">
          <span className="label">Nome da Campanha</span>
          <span className="value" title={name || "Sem nome"}>
            {name || "Sem nome"}
          </span>
        </div>

        <div className="campaign-metric">
          <span className="label">CTR</span>
          <span className="value">{formatNumberBR(ctrNumber)}</span>
        </div>

        <div className="campaign-metric">
          <span className="label">CPC</span>
          <span className="value">{formatNumberBR(cpcNumber)}</span>
        </div>

        <div className="campaign-metric">
          <span className="label">ROAS</span>
          <span className="value">{formatNumberBR(roasNumber)}</span>
        </div>

        <div className="campaign-metric">
          <span className="label">Taxa de Conversão</span>
          <span className="value">{formatNumberBR(conversionRateNumber)}</span>
        </div>

        <div className="campaign-metric-priority">
          <span className="label">Prioridade</span>
          <span className={`value ${getPriorityClass()}`}>
            {prioridade}
          </span>
        </div>
      </div>
      <div className="campaign-item-button">
        <button className="detail-button" onClick={handleClick}>
          <i className="ti ti-chevron-right"></i>
        </button>
      </div>
    </div>
  );
}

function CampaignList({
  onCampaignClick,
  lastUpdated,
  onCampaignsLoaded,
  filterByPriority,
}) {
  const [campaigns, setCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [averages, setAverages] = useState(null);
  const [totalAverages, setTotalAverages] = useState(null);
  const [rawCampaigns, setRawCampaigns] = useState([]);

  const dropdownRef = useRef(null);
  const [filterStartDate, setFilterStartDate] = useState(null);
  const [filterEndDate, setFilterEndDate] = useState(null);
  const [selectedOption, setSelectedOption] = useState("Selecione o período");
  const [showDropdown, setShowDropdown] = useState(false);

  const selectedClientId = localStorage.getItem("selectedClientId");

  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        const response = await fetchWithToken(`https://ad-insights-api-0f2fcea0deda.herokuapp.com/api/campaign-insight/campaigns-file?clientId=${selectedClientId}&funil=funilEcommerce`)
        if (!response.ok) {
          throw new Error(`Erro HTTP: ${response.status}`);
        }

        const data = await response.json();
        const campaignsArray = data?.data?.campaigns || [];

        const formatted = campaignsArray.map((c, index) => ({
          id: index,
          name: c.nomeDaCampanha,
          ctr: c.ctr != null ? String(c.ctr) : "0%",
          cpc: c.cpc != null ? String(c.cpc) : "R$0,00",
          roas: c.roas != null ? String(c.roas) : "R$0,00",
          conversionRate:
            c.taxaDeConversao != null ? String(c.taxaDeConversao) : "0%",
          prioridade: c.prioridade || "N/A",
          compras: c.compras != null ? String(c.compras) : "R$0,00",
        }));

        const averagesData = data?.data?.averages || {};
        const calculatedAverages = calculateAverages(formatted);

        setTotalAverages(averagesData);
        setAverages(calculatedAverages);
        setRawCampaigns(campaignsArray);
        setCampaigns(formatted);
        onCampaignsLoaded?.(formatted);
      } catch (error) {
        console.error("Erro ao buscar campanhas:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCampaigns();
  }, [lastUpdated]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const calculateAverages = (campaigns) => {
    const total = {
      conversionRate: 0,
      ctr: 0,
      cpc: 0,
      roas: 0,
      compras: 0,
    };
    let count = 0;

    campaigns.forEach((c) => {
      total.conversionRate += parsePercentage(c.conversionRate);
      total.ctr += parsePercentage(c.ctr);
      total.cpc += parseCurrency(c.cpc);
      total.roas += parseCurrency(c.roas);
      total.compras += parseCurrency(c.compras);
      count++;
    });

    return {
      avgConversionRate: total.conversionRate / count,
      avgCtr: total.ctr / count,
      avgCpc: total.cpc / count,
      avgCompras: total.compras / count,
      avgRoas: total.roas / count,
    };
  };

  const nonZeroMetrics = (c) =>
    c.ctr !== "0%" ||
    c.cpc !== "R$0,00" ||
    c.roas !== "R$0,00" ||
    c.conversionRate !== "0%" ||
    c.compras !== "R$0,00";

  const clearDateFilter = () => {
    setFilterStartDate(null);
    setFilterEndDate(null);
    setSelectedOption("Selecione o período");
  };

  const campaignsWithDates = campaigns.map((c, idx) => {
    const raw = rawCampaigns[idx] || {};
    return {
      ...c,
      inicioDate: parseDate(raw.inicioDosRelatorios),
      terminoDate: parseDate(raw.terminoDosRelatorios),
    };
  });

  let byPriority = campaignsWithDates;
  if (filterByPriority) {
    byPriority = campaignsWithDates.filter((c) =>
      c.prioridade.toLowerCase().includes(filterByPriority.toLowerCase())
    );
  }

  const filteredCampaigns = byPriority.filter((c) => {
    if (!filterStartDate && !filterEndDate) return true;

    if (filterStartDate && filterEndDate) {
      return c.inicioDate <= filterEndDate && c.terminoDate >= filterStartDate;
    }
    if (filterStartDate) {
      return c.terminoDate >= filterStartDate;
    }
    if (filterEndDate) {
      return c.inicioDate <= filterEndDate;
    }
    return true;
  });

  const handleOptionClick = (option) => {
    const today = new Date();

    if (option === "Últimos 7 dias") {
      setFilterStartDate(new Date(today.setDate(today.getDate() - 7)));
      setFilterEndDate(new Date());
      setSelectedOption("Últimos 7 dias");
      setShowDropdown(false);
    } else if (option === "Últimos 15 dias") {
      setFilterStartDate(new Date(today.setDate(today.getDate() - 15)));
      setFilterEndDate(new Date());
      setSelectedOption("Últimos 15 dias");
      setShowDropdown(false);
    } else if (option === "Últimos 30 dias") {
      setFilterStartDate(new Date(today.setDate(today.getDate() - 30)));
      setFilterEndDate(new Date());
      setSelectedOption("Últimos 30 dias");
      setShowDropdown(false);
    } else if (option === "Personalizado") {
      setFilterStartDate(null);
      setFilterEndDate(null);
      setSelectedOption("Personalizado");
      setShowDropdown(true);
    }
  };

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  if (isLoading) {
    return (
      <div className="loading">
        <p>Carregando campanhas...</p>
      </div>
    );
  }

  return (
    <div className="campaign-list">

      <div className="date-filter-container">
        {selectedOption !== "Selecione o período" && (
          <div className="date-filter-container">
            <button className="clear-filter-button" onClick={clearDateFilter}>
              Limpar filtro de datas
            </button>
          </div>
        )}
        <div className="date-filter" ref={dropdownRef}>
          <div className="date-range" onClick={toggleDropdown}>
            <span>{selectedOption}</span>
            <div className="dropdown-button">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M7 10L12 15M12 15L17 10" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          </div>
          {showDropdown && (
            <div className="date-dropdown">
              {["Últimos 7 dias", "Últimos 15 dias", "Últimos 30 dias", "Personalizado"].map((option) => (
                <div key={option} className="date-option" onClick={() => handleOptionClick(option)}>
                  {option}
                </div>
              ))}

              {selectedOption === "Personalizado" && (
                <div className="custom-date-inputs">
                  <label className="date-option">Início do Relatório:
                    <input
                      type="date"
                      value={filterStartDate?.toISOString().slice(0, 10) || ""}
                      onChange={(e) => setFilterStartDate(parseIsoToLocalDate(e.target.value))}
                    />
                  </label>
                  <label className="date-option">Fim do Relatório:
                    <input
                      type="date"
                      value={filterEndDate?.toISOString().slice(0, 10) || ""}
                      onChange={(e) => setFilterEndDate(parseIsoToLocalDate(e.target.value))}
                    />
                  </label>
                  <button onClick={() => setShowDropdown(false)}>Aplicar</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {filteredCampaigns.length > 0 ? (
        filteredCampaigns.filter(nonZeroMetrics).map((campaign) => (
          <CampaignItem
            key={campaign.id}
            allCampaigns={campaignsWithDates}
            campaign={campaign}
            rawCampaign={rawCampaigns[campaign.id]}
            averages={averages}
            totalAverages={totalAverages}
            onClick={onCampaignClick}
          />
        ))
      ) : (
        <div className="no-results">
          <i className="ti ti-search-off"></i>
          <p>Nenhuma campanha encontrada.</p>
        </div>
      )}
    </div>
  );
}

export default CampaignList;
